import React, { useEffect, useState } from "react";
import StatusDropdown from "../Shared/StatusDropdown";
import { dropdownBlogs, dropdownCategories } from "../../api/dropdownService";
import { addSeo, updateSeo } from "../../api/SeoService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SeoFields from "../../common/SEOFields";
import { useNavigate } from "react-router-dom";

const defaultValues = {
  title: "",
  slug: "",
  description: "",
  category: "",
  productCategory: "",
  author: "",
  readingTime: "",
  status: "ACTIVE",
  coverImage: null,
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  metaImage: null,
};
const BlogForm = ({
  blog,
  initialValues,
  onSubmit,
  onSuccess,
  loading,
  onCancel,
  errors = {},
  backendErrors = {},
}) => {
  const navigate = useNavigate();
  const [productCategories, setProductCategories] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [isMetaTitleEdited, setIsMetaTitleEdited] = useState(false);
  const [backendErrorsState, setBackendErrorsState] = useState({});
  const [formData, setFormData] = useState({
    ...defaultValues,
    ...(initialValues || {}),
  });
  const staticAuthors = [
    {
      id: "nameplatedesign-admin",
      name: "NameplateDesign Admin",
    },
  ];
  const authors = staticAuthors;
  useEffect(() => {
    loadCategories();
  }, []);
  useEffect(() => {
    setFormData({
      ...defaultValues,
      ...(initialValues || {}),
      metaTitle:
        initialValues?.metaTitle ||
        initialValues?.seoTitle ||
        initialValues?.meta_title ||
        initialValues?.seo_title ||
        initialValues?.seo?.title ||
        initialValues?.seo?.metaTitle ||
        initialValues?.seo?.seoTitle ||
        initialValues?.seo?.meta_title ||
        initialValues?.seo?.seo_title ||
        "",
      metaDescription:
        initialValues?.metaDescription ||
        initialValues?.seoDescription ||
        initialValues?.meta_description ||
        initialValues?.seo_description ||
        initialValues?.seo?.description ||
        initialValues?.seo?.metaDescription ||
        initialValues?.seo?.seoDescription ||
        initialValues?.seo?.meta_description ||
        initialValues?.seo?.seo_description ||
        "",
      metaKeywords: Array.isArray(initialValues?.metaKeywords)
        ? initialValues.metaKeywords.join(", ")
        : Array.isArray(initialValues?.meta_keywords)
          ? initialValues?.meta_keywords.join(", ")
          : initialValues?.metaKeywords ||
            initialValues?.meta_keywords ||
            initialValues?.seo?.metaKeywords ||
            initialValues?.seo?.keywords ||
            "",
      metaImage:
        initialValues?.metaImage ||
        initialValues?.metaImageUrl ||
        initialValues?.meta_image ||
        initialValues?.meta_image_url ||
        initialValues?.seo?.image ||
        initialValues?.seo?.metaImage ||
        initialValues?.seo?.meta_image ||
        initialValues?.seo?.imageUrl ||
        initialValues?.seo?.metaImageUrl ||
        "",
      metaImagePreview:
        initialValues?.metaImage ||
        initialValues?.metaImageUrl ||
        initialValues?.meta_image ||
        initialValues?.meta_image_url ||
        initialValues?.seo?.image ||
        initialValues?.seo?.metaImage ||
        initialValues?.seo?.meta_image ||
        initialValues?.seo?.imageUrl ||
        initialValues?.seo?.metaImageUrl ||
        "",
    });
    setIsMetaTitleEdited(
      !!(
        initialValues?.metaTitle ||
        initialValues?.seoTitle ||
        initialValues?.meta_title ||
        initialValues?.seo_title ||
        initialValues?.seo?.title ||
        initialValues?.seo?.metaTitle ||
        initialValues?.seo?.seoTitle ||
        initialValues?.seo?.meta_title ||
        initialValues?.seo?.seo_title
      ),
    );
  }, [initialValues]);
  const loadCategories = async () => {
    try {
      const [productRes, blogRes] = await Promise.all([dropdownCategories(), dropdownBlogs()]);
      setProductCategories(productRes?.data || []);
      setBlogCategories(blogRes?.data || []);
    } catch (error) {
      console.log(error);

      const responseErrors = error?.response?.data?.error ||
        error?.response?.data?.errors ||
        error?.response?.data ||
        error?.error || { message: error?.message || "Submit failed" };

      setBackendErrorsState(responseErrors);
    }
  };
  const mergedBackendErrors = {
    ...backendErrors,
    ...backendErrorsState,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      if (name === "title" && !isMetaTitleEdited) {
        updated.metaTitle = value;
      }
      return updated;
    });
    if (name === "metaTitle") {
      setIsMetaTitleEdited(true);
    }
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        resolve("");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || "");
      reader.onerror = () => reject(new Error("Failed to read image file"));
      reader.readAsDataURL(file);
    });
  };

  const convertToPlainText = (html) =>
    (html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    setBackendErrorsState({});

    const blogDescription = convertToPlainText(formData.description);

    const blogPayload = {
      title: formData.title,
      slug: formData.slug,
      description: blogDescription,
      blogDescription,
      category: formData.category,
      productCategory: formData.productCategory,
      author: formData.author,
      readingTime: formData.readingTime,
      status: formData.status,
    };

    // =====================
    // BLOG API
    // =====================

    const blogResponse = await onSubmit(blogPayload);

    console.log("BLOG RESPONSE:", blogResponse);

    if (!blogResponse?.success) {
      const blogError =
        blogResponse?.error ||
        blogResponse?.data?.error ||
        blogResponse?.data?.errors ||
        blogResponse?.errors || {
          message: "Blog update failed",
        };

      setBackendErrorsState(blogError);
      return;
    }

    // =====================
    // GET BLOG ID
    // =====================

    const blogId =
      blog?._id ||
      formData._id ||
      initialValues?._id ||
      blogResponse?.data?._id ||
      blogResponse?.data?.data?._id ||
      blogResponse?.data?.blog?._id ||
      blogResponse?._id;

    if (!blogId) {
      setBackendErrorsState({
        message: "Blog id not found",
      });
      return;
    }

    // =====================
    // SEO
    // =====================

    const isEditMode = Boolean(
      blog?._id ||
        formData._id ||
        initialValues?._id
    );

    const seoPayload = {
      referenceId: String(blogId),
      referenceType: "Blog",

      metaTitle: formData.metaTitle || "",

      metaDescription: formData.metaDescription || "",

      metaKeywords: Array.isArray(formData.metaKeywords)
        ? formData.metaKeywords
        : (formData.metaKeywords || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),

      status: formData.status || "",

      metaImage:
        typeof formData.metaImage === "string"
          ? formData.metaImage
          : formData.metaImage
            ? await readFileAsBase64(formData.metaImage)
            : "",
    };

    console.log("SEO PAYLOAD:", seoPayload);

    const seoResponse = isEditMode
      ? await updateSeo(seoPayload)
      : await addSeo(seoPayload);

    console.log("SEO RESPONSE:", seoResponse);

    if (!seoResponse?.success) {
      const seoError =
        seoResponse?.error ||
        seoResponse?.data?.error ||
        seoResponse?.data?.errors ||
        seoResponse?.errors || {
          message: "SEO update failed",
        };

      setBackendErrorsState(seoError);
      return;
    }

    // =====================
    // SUCCESS
    // =====================

    console.log("BLOG + SEO SUCCESS");

    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    console.error("SUBMIT ERROR:", error);

    const responseData = error?.response?.data;

    const responseErrors =
      responseData?.error ||
      responseData?.errors ||
      responseData ||
      error?.error || {
        message: error?.message || "Submit failed",
      };

    const errorState = {
      ...(
        typeof responseErrors === "object"
          ? responseErrors
          : {}
      ),

      message:
        responseData?.message ||
        responseErrors?.message ||
        error?.message ||
        "Submit failed",
    };

    setBackendErrorsState(errorState);
  }
};
  return (
    <div className="container-fluid p-4">
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            
            {mergedBackendErrors?.message && (
              <div className="alert alert-danger">{mergedBackendErrors.message}</div>
            )}
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label">
                  Blog Title
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  className={`form-control ${
                    errors.title || mergedBackendErrors?.title ? "is-invalid" : ""
                  }`}
                  value={formData.title}
                  onChange={handleChange}
                />
                {(errors.title || mergedBackendErrors?.title) && (
                  <div className="invalid-feedback">
                    {errors.title || mergedBackendErrors.title}
                  </div>
                )}
              </div>
              <div className="col-md-12 mb-4">
                <label className="form-label">Blog Description</label>
                <ReactQuill
                  theme="snow"
                  value={formData.description}
                  onChange={(value) => {
                    const plainText = value
                      .replace(/<[^>]*>/g, "")
                      .replace(/\s+/g, " ")
                      .trim();
                    setFormData((prev) => ({
                      ...prev,
                      description: value,
                      metaDescription: plainText.substring(0, 160),
                    }));
                  }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Blog Category<span className="text-danger">*</span>
                </label>
                <select
                  name="category"
                  className={`form-select ${
                    errors.category || mergedBackendErrors?.category ? "is-invalid" : ""
                  }`}
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">Select Category</option>
                  {blogCategories.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {(errors.category || mergedBackendErrors?.category) && (
                  <div className="invalid-feedback d-block">
                    {errors.category || mergedBackendErrors.category}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Product Category<span className="text-danger">*</span>
                </label>
                <select
                  name="productCategory"
                  className={`form-select ${
                    errors.productCategory || mergedBackendErrors?.productCategory
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formData.productCategory}
                  onChange={handleChange}
                >
                  <option value="">Select Product Category</option>
                  {productCategories.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {(errors.productCategory || mergedBackendErrors?.productCategory) && (
                  <div className="invalid-feedback d-block">
                    {errors.productCategory || mergedBackendErrors.productCategory}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Author<span className="text-danger">*</span>
                </label>
                <select
                  name="author"
                  className={`form-select ${
                    errors.author || mergedBackendErrors?.author ? "is-invalid" : ""
                  }`}
                  value={formData.author}
                  onChange={handleChange}
                >
                  <option value="">Select Author</option>
                  {authors.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {(errors.author || mergedBackendErrors?.author) && (
                  <div className="invalid-feedback d-block">
                    {errors.author || mergedBackendErrors.author}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Reading Time</label>
                <input
                  type="text"
                  name="readingTime"
                  className="form-control"
                  value={formData.readingTime}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <StatusDropdown
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                 />
              </div>
              <SeoFields
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                errors={errors}
                backendErrors={mergedBackendErrors}
              />
            </div>
            <div className="text-end">
              <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                {loading ? "Saving..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn"
                style={{ backgroundColor: "#032B4D", color: "#fff" }}
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default BlogForm;
